import React from 'react';
import { useState, useEffect, useRef } from "react";
import { GoogleGenerativeAI } from "@google/generative-ai";

// ✅ Gemini API setup
const API_KEY = "AIzaSyBZaqKBTZTUds0ptmklR41mjS36PRIDIAU";
const googleai = new GoogleGenerativeAI(API_KEY);
const gemini = googleai.getGenerativeModel({ model: "gemini-2.0-flash" });

// Icons using SVG (replacing Lucide React)
const MessageIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
);

const CloseIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
);

const SendIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="22" y1="2" x2="11" y2="13" />
        <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
);

const BotIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="11" width="18" height="10" rx="2" />
        <circle cx="12" cy="5" r="2" />
        <path d="M12 7v4" />
        <line x1="8" y1="16" x2="8" y2="16" />
        <line x1="16" y1="16" x2="16" y2="16" />
    </svg>
);

const UserIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
        <circle cx="12" cy="7" r="4" />
    </svg>
);

const Chatbot = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([
        { role: "assistant", content: "Hi! I'm your smart assistant. How can I help you today?" }
    ]);
    const [chat, setChat] = useState(null);
    const [input, setInput] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const messagesEndRef = useRef(null);

    // Auto-scroll to bottom when new messages arrive
    const scrollToBottom = () => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    // Start Gemini chat
    useEffect(() => {
        async function initChat() {
            try {
                const newChat = gemini.startChat({ history: [] });
                setChat(newChat);
            } catch (error) {
                console.error("Error initializing chat:", error);
            }
        }
        initChat();
    }, []);

    const sendMessage = async () => {
        if (!input.trim() || isLoading) return;

        const userMsg = { role: "user", content: input };
        setMessages((prev) => [...prev, userMsg]);
        setInput("");
        setIsLoading(true);

        try {
            const result = await chat.sendMessage(input);
            const responseText = result.response.text();

            const botMsg = { role: "assistant", content: responseText };
            setMessages((prev) => [...prev, botMsg]);
        } catch (error) {
            console.error("Error:", error);
            setMessages((prev) => [
                ...prev,
                { role: "system", content: "Something went wrong. Please try again!" }
            ]);
        } finally {
            setIsLoading(false);
        }
    };

    // Get current time for the chat header
    const getCurrentTime = () => {
        const now = new Date();
        return now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    // Get current date for the chat header
    const getCurrentDate = () => {
        const now = new Date();
        return now.toLocaleDateString();
    };

    return (
        <div className="chatbot-container">
            {/* Floating Button */}
            {!isOpen && (
                <button
                    onClick={() => setIsOpen(true)}
                    className="chatbot-toggle-btn"
                    aria-label="Open chat"
                >
                    <MessageIcon />
                </button>
            )}

            {/* Chat Window */}
            {isOpen && (
                <div className="chatbot-window">
                    {/* Header */}
                    <div className="chatbot-header">
                        <div className="chatbot-header-info">
                            <div className="chatbot-avatar">
                                <BotIcon />
                            </div>
                            <div>
                                <h3>Chat Assistant</h3>
                                <div className="status-online">Online</div>
                            </div>
                        </div>
                        <div className="chatbot-header-time">
                            <div>{getCurrentTime()}</div>
                            <div>{getCurrentDate()}</div>
                        </div>
                        <button
                            onClick={() => setIsOpen(false)}
                            className="chatbot-close-btn"
                            aria-label="Close chat"
                        >
                            <CloseIcon />
                        </button>
                    </div>

                    {/* Messages */}
                    <div className="chatbot-messages">
                        {messages.map((msg, i) => (
                            <div
                                key={i}
                                className={`message ${msg.role === "user" ? "user-message" : "assistant-message"}`}
                            >
                                <div className="message-content">
                                    <div className="message-icon">
                                        {msg.role === "assistant" ? <BotIcon /> : <UserIcon />}
                                    </div>
                                    <div className="message-text">
                                        {msg.content}
                                    </div>
                                </div>
                            </div>
                        ))}

                        {isLoading && (
                            <div className="message assistant-message">
                                <div className="message-content">
                                    <div className="message-icon">
                                        <BotIcon />
                                    </div>
                                    <div className="typing-indicator">
                                        <span></span>
                                        <span></span>
                                        <span></span>
                                    </div>
                                </div>
                            </div>
                        )}
                        <div ref={messagesEndRef} className="messages-end"></div>
                    </div>

                    {/* Input */}
                    <div className="chatbot-input-container">
                        <input
                            type="text"
                            placeholder="Type your message..."
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            className="chatbot-input"
                            onKeyDown={(e) => e.key === "Enter" && sendMessage()}
                            disabled={isLoading}
                        />
                        <button
                            onClick={sendMessage}
                            className="chatbot-send-btn"
                            disabled={isLoading || !input.trim()}
                            aria-label="Send message"
                        >
                            <SendIcon />
                        </button>
                    </div>
                </div>
            )}

               <style>{`
                .chatbot-container {
                    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif;
                }
                
                .chatbot-toggle-btn {
                    position: fixed;
                    bottom: 24px;
                    right: 24px;
                    width: 60px;
                    height: 60px;
                    background: linear-gradient(135deg, #6e8efb, #a777e3);
                    color: white;
                    border: none;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
                    transition: all 0.3s ease;
                    z-index: 1000;
                }
                
                .chatbot-toggle-btn:hover {
                    transform: scale(1.05);
                    box-shadow: 0 6px 25px rgba(0, 0, 0, 0.2);
                }
                
                .chatbot-window {
                    position: fixed;
                    bottom: 24px;
                    right: 24px;
                    width: 360px;
                    height: 500px;
                    background: white;
                    border-radius: 16px;
                    display: flex;
                    flex-direction: column;
                    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
                    overflow: hidden;
                    z-index: 1000;
                }
                
                .chatbot-header {
                    background: linear-gradient(135deg, #6e8efb, #a777e3);
                    color: white;
                    padding: 16px 20px;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }
                
                .chatbot-header-info {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                }
                
                .chatbot-avatar {
                    width: 40px;
                    height: 40px;
                    background: white;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #6e8efb;
                }
                
                .chatbot-header-info h3 {
                    margin: 0;
                    font-size: 16px;
                    font-weight: 600;
                }
                
                .status-online {
                    font-size: 12px;
                    opacity: 0.9;
                    display: flex;
                    align-items: center;
                }
                
                .status-online:before {
                    content: '';
                    display: inline-block;
                    width: 8px;
                    height: 8px;
                    background: #4ade80;
                    border-radius: 50%;
                    margin-right: 6px;
                }
                
                .chatbot-header-time {
                    text-align: right;
                    font-size: 12px;
                    opacity: 0.9;
                }
                
                .chatbot-close-btn {
                    background: none;
                    border: none;
                    color: white;
                    cursor: pointer;
                    padding: 4px;
                    border-radius: 4px;
                    margin-left: 12px;
                }
                
                .chatbot-close-btn:hover {
                    background: rgba(255, 255, 255, 0.2);
                }
                
                .chatbot-messages {
                    flex: 1;
                    padding: 20px;
                    overflow-y: auto;
                    background: #f8fafc;
                    display: flex;
                    flex-direction: column;
                    gap: 16px;
                }
                
                .message {
                    display: flex;
                }
                
                .user-message {
                    justify-content: flex-end;
                }
                
                .assistant-message {
                    justify-content: flex-start;
                }
                
                .message-content {
                    display: flex;
                    align-items: flex-start;
                    max-width: 80%;
                    gap: 8px;
                }
                
                .user-message .message-content {
                    flex-direction: row-reverse;
                }
                
                .message-icon {
                    width: 32px;
                    height: 32px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                }
                
                .assistant-message .message-icon {
                    background: #e0e7ff;
                    color: #6366f1;
                }
                
                .user-message .message-icon {
                    background: #6366f1;
                    color: white;
                }
                
                .message-text {
                    padding: 12px 16px;
                    border-radius: 18px;
                    font-size: 14px;
                    line-height: 1.5;
                }
                
                .assistant-message .message-text {
                    background: white;
                    color: #374151;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
                    border-top-left-radius: 4px;
                }
                
                .user-message .message-text {
                    background: #6366f1;
                    color: white;
                    border-top-right-radius: 4px;
                }
                
                .typing-indicator {
                    display: flex;
                    align-items: center;
                    background: white;
                    padding: 12px 16px;
                    border-radius: 18px;
                    border-top-left-radius: 4px;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
                }
                
                .typing-indicator span {
                    width: 8px;
                    height: 8px;
                    background: #9ca3af;
                    border-radius: 50%;
                    display: inline-block;
                    margin: 0 2px;
                    animation: typing 1.4s infinite both;
                }
                
                .typing-indicator span:nth-child(2) {
                    animation-delay: 0.2s;
                }
                
                .typing-indicator span:nth-child(3) {
                    animation-delay: 0.4s;
                }
                
                @keyframes typing {
                    0%, 60%, 100% {
                        transform: translateY(0);
                    }
                    30% {
                        transform: translateY(-5px);
                    }
                }
                
                .messages-end {
                    height: 1px;
                }
                
                .chatbot-input-container {
                    padding: 16px;
                    background: white;
                    border-top: 1px solid #e5e7eb;
                    display: flex;
                    align-items: center;
                    gap: 12px;
                }
                
                .chatbot-input {
                    flex: 1;
                    padding: 12px 16px;
                    border: 1px solid #e5e7eb;
                    border-radius: 24px;
                    font-size: 14px;
                    outline: none;
                    transition: border-color 0.2s;
                }
                
                .chatbot-input:focus {
                    border-color: #6366f1;
                }
                
                .chatbot-send-btn {
                    width: 44px;
                    height: 44px;
                    background: #6366f1;
                    color: white;
                    border: none;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    transition: background 0.2s;
                }
                
                .chatbot-send-btn:hover:not(:disabled) {
                    background: #4f46e5;
                }
                
                .chatbot-send-btn:disabled {
                    background: #9ca3af;
                    cursor: not-allowed;
                }
                
                @media (max-width: 480px) {
                    .chatbot-window {
                        width: 100%;
                        height: 100%;
                        bottom: 0;
                        right: 0;
                        border-radius: 0;
                    }
                    
                    .chatbot-toggle-btn {
                        bottom: 20px;
                        right: 20px;
                    }
                }
            `}</style>
        </div>
    );
};

export default Chatbot;